import React, { useCallback, useEffect, useState } from 'react';
import { usePos } from '../context/PosContext';
import {
  listBranches,
  getSalesRange,
  getBranchProducts,
  summariseByBranch,
  BranchRow,
  BranchProductsRow,
  BranchSalesSummary,
} from '../services/headOffice';
import { formatDateTime } from '../services/datetime';
import './Sections.css';

// Head Office: a consolidated cross-branch view fed by each shop's LAN server
// pushing sales/stock to the shared cloud (see server/cloud.js, headOffice.ts).
// Read-only and online-only — it needs the internet to reach the cloud, unlike
// the offline-first in-shop screens.

const startOfDay = (d: string) => `${d}T00:00:00.000Z`;
const endOfDay = (d: string) => `${d}T23:59:59.999Z`;

const HeadOffice: React.FC = () => {
  const { formatMoney } = usePos();
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);

  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [summary, setSummary] = useState<{
    perBranch: BranchSalesSummary[];
    totalBills: number;
    totalAmount: number;
  }>({ perBranch: [], totalBills: 0, totalAmount: 0 });
  const [stock, setStock] = useState<BranchProductsRow[]>([]);
  const [showStock, setShowStock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const branchName = useCallback(
    (id: string) => branches.find((b) => b.branch_id === id)?.name || id,
    [branches]
  );

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const [b, sales] = await Promise.all([
        listBranches(),
        getSalesRange(startOfDay(from), endOfDay(to)),
      ]);
      setBranches(b);
      setSummary(summariseByBranch(sales));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const loadStock = async () => {
    setShowStock(true);
    try {
      setStock(await getBranchProducts());
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Low-stock items across all branches (stock at or below reorder level, or 5).
  const lowStock = stock.flatMap((br) =>
    (br.data || [])
      .filter(
        (p) =>
          p.stockable !== false &&
          typeof p.stock === 'number' &&
          p.stock <= (p.reorderLevel ?? 5)
      )
      .map((p) => ({ branch: br.name || br.branch_id, name: p.name, stock: p.stock as number, reorder: p.reorderLevel ?? 5 }))
  );

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>HEAD OFFICE</h1>
        <span style={{ marginLeft: 'auto', color: 'var(--color-muted)', fontSize: 13 }}>
          {branches.length} branch{branches.length === 1 ? '' : 'es'}
        </span>
      </div>

      <div className="filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label>
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button className="btn-teal" onClick={load} disabled={busy}>
          {busy ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <p className="empty-hint" style={{ color: '#d21f3c' }}>
          {error}
        </p>
      )}

      {/* ---- Branch heartbeats ---- */}
      <h2 style={{ fontSize: 18, marginTop: 20 }}>Branches</h2>
      {branches.length === 0 ? (
        <p className="empty-hint">No branches have checked in yet.</p>
      ) : (
        <table className="section-table">
          <thead>
            <tr>
              <th>Branch</th>
              <th>Last Seen</th>
              <th className="right">Today's Sales</th>
            </tr>
          </thead>
          <tbody>
            {branches.map((b) => (
              <tr key={b.branch_id}>
                <td>{b.name || b.branch_id}</td>
                <td>{formatDateTime(b.last_seen)}</td>
                <td className="right">{formatMoney(Number(b.today_total) || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ---- Consolidated sales for the range ---- */}
      <h2 style={{ fontSize: 18, marginTop: 24 }}>
        Consolidated Sales ({from === to ? from : `${from} → ${to}`})
      </h2>
      <div style={{ display: 'flex', gap: 16, margin: '8px 0 12px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Bills', value: String(summary.totalBills) },
          { label: 'Total Amount', value: formatMoney(summary.totalAmount) },
        ].map((c) => (
          <div
            key={c.label}
            style={{
              background: 'var(--color-surface, #fff)',
              border: '1px solid var(--color-border, #e0e0e0)',
              borderRadius: 10,
              padding: '12px 20px',
              minWidth: 160,
            }}
          >
            <div style={{ color: 'var(--color-muted)', fontSize: 13 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{c.value}</div>
          </div>
        ))}
      </div>
      {summary.perBranch.length === 0 ? (
        <p className="empty-hint">No sales in this range.</p>
      ) : (
        <table className="section-table">
          <thead>
            <tr>
              <th>Branch</th>
              <th className="right">Bills</th>
              <th className="right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {summary.perBranch.map((s) => (
              <tr key={s.branchId}>
                <td>{branchName(s.branchId)}</td>
                <td className="right">{s.bills}</td>
                <td className="right">{formatMoney(s.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ---- Cross-branch low stock ---- */}
      <h2 style={{ fontSize: 18, marginTop: 24 }}>Cross-Branch Stock</h2>
      {!showStock ? (
        <button className="btn-grey" onClick={loadStock}>
          Load Low-Stock Report
        </button>
      ) : lowStock.length === 0 ? (
        <p className="empty-hint">No low-stock items reported across branches.</p>
      ) : (
        <table className="section-table">
          <thead>
            <tr>
              <th>Branch</th>
              <th>Item</th>
              <th className="right">Stock</th>
              <th className="right">Reorder</th>
            </tr>
          </thead>
          <tbody>
            {lowStock.map((r, i) => (
              <tr key={i}>
                <td>{r.branch}</td>
                <td>{r.name}</td>
                <td className="right" style={{ color: r.stock <= 0 ? '#d21f3c' : '#d08a1a' }}>
                  {r.stock}
                </td>
                <td className="right">{r.reorder}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default HeadOffice;
